/**
 * How long a background job (a big delete, restore, Empty Bin, Delete forever…)
 * is followed, and how late one may still START — shared by the SPA's tracker
 * (`features/files/client/job-tracker.ts`) and the worker's pickup checks, so the
 * two can never drift. Dependency-free: the worker must never import client code.
 */

/**
 * The client's tracker gives up on a job (outcome unknown) this long after the
 * 202, however far the job has got. Past the server's marker age (an hour), so a
 * live job is never given up on.
 */
export const JOB_DEADLINE_MS = 65 * 60_000

/**
 * A job must not START so late that it would still be running when the tracker
 * gives up (`JOB_DEADLINE_MS` after the 202): a job kind that checks its age at
 * pickup refuses, doing nothing, once it is older than
 * `JOB_DEADLINE_MS - JOB_PICKUP_MARGIN_MS` (`jobPickupExpired`).
 *
 * This covers a worker outage and any job left waiting past the window. It does
 * not cover jobs left queued by a rollback (the runbook drops those).
 *
 * Accepted residual: a job that starts just inside the window and runs longer
 * than the margin can still finish after the person was told "outcome unknown".
 * That message says to check, so it stays honest.
 */
export const JOB_PICKUP_MARGIN_MS = 15 * 60_000

/** Is a job queued at `requestedAtMs` too old to start now? */
export function jobPickupExpired(requestedAtMs: number, nowMs: number = Date.now()): boolean {
  return nowMs - requestedAtMs > JOB_DEADLINE_MS - JOB_PICKUP_MARGIN_MS
}

/** The refusal a job gives when it was picked up too late to run (`jobPickupExpired`). */
export function jobExpiredBeforeRunMessage(): string {
  return 'This request expired before it ran — try again.'
}

/** The stored answer of a `file-move` job (#1167 S4) picked up too late to run: nothing moved. The client reads it
 *  like any refusal (`MoveRefusedError`), so a person following the job is told — never left waiting. */
export const MOVE_JOB_EXPIRED_CODE = 'MOVE_JOB_EXPIRED'

/** The stored answer of a `file-move` job (#1167 S4) whose source folder was moved elsewhere (by another path)
 *  between the 202 and the job's pickup: nothing moved. Distinct from #982's `MOVE_SOURCE_CHANGED` (the source was
 *  EDITED mid-move across organisations), which says something else to the person. */
export const MOVE_SOURCE_MOVED_CODE = 'MOVE_SOURCE_MOVED'

/** A big move's `file-move` job (#1167 S4) whose outcome the client could not learn: its status route stopped
 *  answering (404 past the "not visible yet" grace — never queued, or its result gone) or the shared deadline
 *  passed while it was still queued or running. Unlike #982's `FOLDER_MOVE_STATUS_UNKNOWN` (the job FINISHED,
 *  its result was lost), nothing is known: the move may be queued, running, done or never run. */
export const MOVE_JOB_STATUS_UNKNOWN_CODE = 'MOVE_JOB_STATUS_UNKNOWN'
